import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../lib/cn';
import { useFieldId } from '../lib/useId';
import { describedBy, errorId, hintId } from './Field';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  containerClassName?: string;
}

const boxClasses =
  'mt-0.5 h-4 w-4 shrink-0 rounded-sm border-line-strong bg-surface text-primary ' +
  'accent-[hsl(var(--primary))] disabled:cursor-not-allowed disabled:opacity-50';

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, hint, error, containerClassName, className, id: providedId, ...props },
  ref,
) {
  const id = useFieldId(providedId);

  return (
    <div className={cn('flex flex-col gap-1', containerClassName)}>
      <div className="flex items-start gap-2">
        <input
          ref={ref}
          id={id}
          type="checkbox"
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(id, hint, error)}
          className={cn(boxClasses, error && 'border-danger', className)}
          {...props}
        />
        <label htmlFor={id} className="cursor-pointer select-none text-base text-ink">
          {label}
        </label>
      </div>
      {hint && (
        <p id={hintId(id)} className="ml-6 text-xs text-ink-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId(id)} role="alert" className="ml-6 text-xs font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  );
});

export interface CheckboxGroupProps {
  legend: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  className?: string;
  children: ReactNode;
}

/** Several related checkboxes. A `<fieldset>` so the group has one accessible name. */
export function CheckboxGroup({ legend, hint, error, className, children }: CheckboxGroupProps) {
  return (
    <fieldset className={cn('flex flex-col gap-2', className)}>
      <legend className="mb-1 text-sm font-medium text-ink">{legend}</legend>
      {hint && <p className="text-xs text-ink-muted">{hint}</p>}
      <div className="flex flex-col gap-2">{children}</div>
      {error && (
        <p role="alert" className="text-xs font-medium text-danger">
          {error}
        </p>
      )}
    </fieldset>
  );
}
